import React, { useEffect, useMemo, useRef, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";

// One colour per series, indexed by position, so a chart with several datasets is readable
// without the publisher having to choose colours. An explicit series colour still wins. The
// first entry is the previous single fallback, which keeps every existing one-series chart
// looking exactly as it did. Hues are ordered most-distinct-first and avoid relying on a
// red/green distinction.
const SERIES_PALETTE = [
  "#0891b2", // cyan
  "#f59e0b", // amber
  "#a78bfa", // violet
  "#34d399", // emerald
  "#fb7185", // rose
  "#60a5fa", // blue
  "#facc15", // yellow
  "#94a3b8", // slate
];

const seriesColor = (color, index) => color || SERIES_PALETTE[index % SERIES_PALETTE.length];

export default function AgentXYChart({ rows = [], config = {}, selectedTime = null }) {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const timeMarkerRef = useRef(null);
  const [hiddenSeries, setHiddenSeries] = useState({});
  const type = config.type;
  const categoryKey = config.categoryKey || "category";
  const valueKey = config.valueKey || "value";
  const seriesConfig = type === "multiline" ? (config.series || []).filter((series) => series?.key) : [{
    key: valueKey,
    label: config.valueLabel || valueKey,
    color: config.color,
  }];
  const seriesKey = seriesConfig.map(({ key, label, color }) => `${key}:${label || ""}:${color || ""}`).join("|");
  const data = useMemo(() => rows.map((row) => {
    const next = { ...row, [categoryKey]: String(row?.[categoryKey] ?? "") };
    // A null value means the series has no observation there (e.g. a run that already finished).
    // Number(null) is 0, which would drop the line to zero, so it is left empty and the line ends.
    seriesConfig.forEach(({ key }) => { next[key] = row?.[key] == null ? undefined : Number(row[key]); });
    return next;
  }).filter((row) => row[categoryKey] && (config.preserveGaps || seriesConfig.some(({ key }) => Number.isFinite(row[key])))),
  [rows, categoryKey, seriesKey, config.preserveGaps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!chartRef.current || !data.length || !["bar", "line", "multiline"].includes(type) || !seriesConfig.length) return undefined;
    setHiddenSeries({});
    const chart = am4core.create(chartRef.current, am4charts.XYChart);
    chartInstanceRef.current = chart;
    chart.data = data;
    chart.padding(18, 24, 8, 8);

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = categoryKey;
    categoryAxis.renderer.grid.template.disabled = true;
    const categoryLabels = categoryAxis.renderer.labels.template;
    categoryLabels.fill = am4core.color("#cbd5e1");
    // Category labels are whatever the analysis produced - an ID, a date, a binned range like
    // "-18.5 to -17.0". Left at a fixed spacing they overlap into an unreadable band, so the axis is
    // given the room the widest label actually needs. Long labels are slanted, which needs far less
    // horizontal room per label than level text and so keeps more of them on the axis.
    const longestLabel = data.reduce(
      (longest, row) => Math.max(longest, String(row?.[categoryKey] ?? "").length),
      0
    );
    const CHAR_WIDTH = 7; // approx px per character at the axis font size
    if (longestLabel > 6) {
      categoryLabels.rotation = -45;
      categoryLabels.horizontalCenter = "right";
      categoryLabels.verticalCenter = "middle";
      categoryAxis.renderer.minGridDistance = 20;
    } else {
      categoryAxis.renderer.minGridDistance = Math.max(35, longestLabel * CHAR_WIDTH + 12);
    }
    // A label longer than this would crowd the plot however it is angled; it is cut and the full
    // text stays available on hover rather than being lost.
    categoryLabels.truncate = true;
    categoryLabels.maxWidth = 150;
    categoryLabels.tooltipText = `{${categoryKey}}`;
    categoryAxis.title.text = config.categoryLabel || "";
    categoryAxis.title.fill = am4core.color("#94a3b8");

    if (Number.isFinite(selectedTime) && type === "multiline") {
      const marker = categoryAxis.axisRanges.create();
      marker.grid.disabled = false;
      marker.grid.location = 0.5;
      marker.grid.stroke = am4core.color("#f8fafc");
      marker.grid.strokeOpacity = 0.9;
      marker.grid.strokeWidth = 2;
      marker.grid.strokeDasharray = "5,4";
      marker.label.disabled = true;
      timeMarkerRef.current = marker;
    }

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.labels.template.fill = am4core.color("#cbd5e1");
    valueAxis.renderer.grid.template.stroke = am4core.color("#334155");
    valueAxis.title.text = [config.valueLabel, config.valueUnit && `(${config.valueUnit})`].filter(Boolean).join(" ");
    valueAxis.title.fill = am4core.color("#94a3b8");

    seriesConfig.forEach(({ key, label, color }, index) => {
      const series = chart.series.push(type === "bar" ? new am4charts.ColumnSeries() : new am4charts.LineSeries());
      const resolvedColor = seriesColor(color, index);
      series.dataFields.categoryX = categoryKey;
      series.dataFields.valueY = key;
      series.name = label || key;
      series.stroke = am4core.color(resolvedColor);
      series.fill = am4core.color(resolvedColor);
      series.tooltipText = `{${categoryKey}}\n${label || key}: {${key}}`;
      if (type === "bar") {
        series.columns.template.width = am4core.percent(62);
        series.columns.template.fillOpacity = 0.75;
      } else {
        series.strokeWidth = 3;
        if (config.preserveGaps) series.connect = false;
        series.bullets.push(new am4charts.CircleBullet());
      }
    });

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = "none";
    return () => {
      timeMarkerRef.current = null;
      chartInstanceRef.current = null;
      chart.dispose();
    };
  }, [data, type, categoryKey, seriesKey, config.categoryLabel, config.valueLabel, config.valueUnit, config.preserveGaps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const marker = timeMarkerRef.current;
    if (!marker || !Number.isFinite(selectedTime)) return;
    const nearest = data.reduce((closest, row) =>
      !closest || Math.abs(row.time - selectedTime) < Math.abs(closest.time - selectedTime) ? row : closest,
    null);
    if (nearest) marker.category = nearest[categoryKey];
  }, [data, categoryKey, selectedTime]);

  const toggleSeries = (index) => {
    const series = chartInstanceRef.current?.series.getIndex(index);
    if (!series) return;
    const nextHidden = !hiddenSeries[index];
    if (nextHidden) series.hide();
    else series.show();
    setHiddenSeries((current) => ({ ...current, [index]: nextHidden }));
  };

  if (!["bar", "line", "multiline"].includes(type)) return <div className="agent-resultsError">Unsupported chart type.</div>;
  if (!seriesConfig.length || !data.length) return <div className="agent-resultsError">No valid chart rows were provided.</div>;
  return <div className="agent-xyChartLayout">
    <div className="agent-xyChart" ref={chartRef} />
    {type === "multiline" && <div className="agent-xyChartLegend" aria-label="Chart series">
      {seriesConfig.map(({ key, label, color }, index) => <button
        key={`${key}:${index}`}
        type="button"
        className={`agent-xyChartLegendItem${hiddenSeries[index] ? " isHidden" : ""}`}
        onClick={() => toggleSeries(index)}
        aria-pressed={!hiddenSeries[index]}
        title={label || key}
      >
        <span className="agent-xyChartLegendSwatch" style={{ backgroundColor: seriesColor(color, index) }} />
        <span className="agent-xyChartLegendLabel">{label || key}</span>
      </button>)}
    </div>}
  </div>;
}
